import React from 'react';
import { ArrowLeft, Loader2, Mic, PanelLeft, Pause, Play, Sparkles, Trash2, Volume2 } from 'lucide-react';
import { WindowControls } from '../../components/WindowControls';
import { AudioMeter } from './AudioMeter';
import { formatTimestamp } from './useRecorder';
import type { RecordingPhase } from './useRecordingSession';
import { t } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';

interface RecordingTopBarProps {
  phase: RecordingPhase;
  isPaused: boolean;
  seconds: number;
  micLevel: number;
  systemLevel: number;
  /** Karşı tarafın sesi alınıyor mu (web'de isteğe bağlı). */
  hasSystemAudio?: boolean;
  isDarkMode: boolean;
  onToggleSidebar: () => void;
  onBack: () => void;
  onDiscard: () => void;
  onTogglePause: () => void;
  onStop: () => void;
}

/**
 * Kayıt sayfasının kendi üst barı. Normal `TopBar` yerine geçiyor: kayıt
 * sürerken arama/tema/paylaş düğmeleri değil, kaydın kendi kontrolleri
 * lazım. Pencere düğmeleri ortak bileşenden geliyor.
 *
 * Düzen bilerek iki kümeye ayrıldı. Solda "neredeyim + ne oluyor" (gezinme,
 * sayaç, durum), sağda "ne yapabilirim" (seviyeler, duraklat, sil, bitir).
 * Eskiden sayaç da dahil her şey sağda tek sırada eziliyordu; kaydın en
 * önemli bilgisi olan süre, çöp kutusuyla duraklat düğmesinin arasında
 * kayboluyordu.
 */
export const RecordingTopBar: React.FC<RecordingTopBarProps> = ({
  phase,
  isPaused,
  seconds,
  micLevel,
  systemLevel,
  hasSystemAudio = true,
  isDarkMode,
  onToggleSidebar,
  onBack,
  onDiscard,
  onTogglePause,
  onStop,
}) => {
  const isLive = phase === 'live';
  const isFinishing = phase === 'stopping' || phase === 'summarizing';
  const meterActive = isLive && !isPaused;

  const stopLabel =
    phase === 'summarizing'
      ? t('AI özetliyor…', 'AI is summarizing…')
      : phase === 'stopping'
        ? t('Kaydediliyor…', 'Saving…')
        : t('Bitir & Özetle', 'Finish & summarize');

  const statusLabel = isFinishing
    ? stopLabel
    : phase === 'starting'
      ? t('Başlatılıyor', 'Starting')
      : isPaused
        ? t('Duraklatıldı', 'Paused')
        : isLive
          ? t('Kayıt sürüyor', 'Recording')
          : t('Hazır', 'Ready');

  const iconButton = isDarkMode
    ? 'hover:bg-[#1F1F23] text-neutral-400 hover:text-[#F0EEE6]'
    : 'hover:bg-neutral-200/80 text-neutral-600 hover:text-black';

  return (
    <header
      className={`app-drag-region h-12 flex items-center justify-between gap-3 px-3.5 select-none shrink-0 z-20 ${
        isDarkMode ? 'bg-[#111113] text-[#D8D8E0]' : 'bg-[#FCFCFD] text-[#1E2024]'
      }`}
    >
      {/* — Sol: gezinme + durum — */}
      <div className="app-no-drag flex items-center gap-1.5 min-w-0">
        <button
          id="btn-recording-sidebar"
          onClick={onToggleSidebar}
          aria-label={t('Menüyü Daralt/Genişlet', 'Collapse/expand sidebar')}
          title={t('Menüyü Daralt / Genişlet', 'Collapse / expand sidebar')}
          className={`p-2 rounded-md transition-all ${iconButton}`}
        >
          <PanelLeft className="w-5 h-5" />
        </button>

        <button
          id="btn-recording-back"
          onClick={onBack}
          title={t('Geri dön (kayıt arka planda sürer)', 'Go back (recording continues in the background)')}
          className={`p-2 rounded-md transition-all ${iconButton}`}
        >
          <ArrowLeft className="w-4.5 h-4.5" />
        </button>

        <div
          className={`flex items-center gap-2.5 ml-1 pl-2.5 pr-3 py-1.5 rounded-lg min-w-0 ${
            isDarkMode ? 'bg-[#19191C]' : 'bg-neutral-100'
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${
              meterActive ? 'bg-rose-500 animate-pulse' : isPaused ? 'bg-amber-400' : 'bg-neutral-500'
            }`}
          />
          <span
            className={`font-mono text-[13px] tabular-nums shrink-0 ${
              isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
            }`}
          >
            {formatTimestamp(seconds)}
          </span>
          <span className="text-[12px] text-neutral-500 truncate hidden lg:inline">{statusLabel}</span>
        </div>
      </div>

      {/* — Sağ: kaydın kontrolleri — */}
      <div className="app-no-drag flex items-center gap-2 shrink-0">
        <div className="hidden md:flex items-center gap-1.5">
          <AudioMeter
            level={micLevel}
            isActive={meterActive}
            icon={<Mic className="w-3.5 h-3.5" />}
            label={t('Mikrofon (sen)', 'Microphone (you)')}
            isDarkMode={isDarkMode}
          />
          {/* Web'de karşı tarafın sesi yalnızca paylaşılan sekmeden gelir. */}
          {(!IS_WEB || hasSystemAudio) && (
            <AudioMeter
              level={systemLevel}
              isActive={meterActive}
              icon={<Volume2 className="w-3.5 h-3.5" />}
              label={IS_WEB ? t('Toplantı sekmesi (karşı taraf)', 'Meeting tab (other side)') : t('Sistem sesi (karşı taraf)', 'System audio (other side)')}
              isDarkMode={isDarkMode}
            />
          )}
        </div>

        <div className={`flex items-center gap-0.5 md:ml-1 md:pl-2 md:border-l ${
          isDarkMode ? 'border-[#3A3A3E]' : 'border-neutral-200'
        }`}>
          <button
            id="btn-recording-pause"
            onClick={onTogglePause}
            disabled={!isLive}
            title={isPaused ? t('Devam et', 'Resume') : t('Duraklat', 'Pause')}
            aria-label={isPaused ? t('Devam et', 'Resume') : t('Duraklat', 'Pause')}
            className={`p-2 rounded-md transition-colors disabled:opacity-40 ${
              isPaused
                ? 'text-emerald-400 hover:bg-emerald-500/10'
                : 'text-neutral-400 hover:text-[#F0EEE6] hover:bg-neutral-700/40'
            }`}
          >
            {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>

          <button
            id="btn-recording-discard"
            onClick={onDiscard}
            disabled={isFinishing}
            title={IS_WEB ? t('Kaydı sil (saklanmaz)', 'Discard recording (won’t be saved)') : t('Kaydı sil (diske yazılmaz)', 'Discard recording (won’t be saved)')}
            aria-label={t('Kaydı sil', 'Discard recording')}
            className="p-2 rounded-md text-neutral-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <button
          id="btn-recording-stop"
          onClick={onStop}
          disabled={!isLive}
          className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#1DA4BA] hover:bg-[#3AC8E0] disabled:opacity-50 text-[#F0EEE6] text-[13px] font-semibold transition-all active:scale-95 shadow-sm"
        >
          {isFinishing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          <span>{stopLabel}</span>
        </button>

        <WindowControls isDarkMode={isDarkMode} />
      </div>
    </header>
  );
};
